import React, { useEffect, useState } from 'react';
// Api
import client from '../../../api/client';
// Constants
import {
  GET_ALL_DOMINATION_FROMS_API,
  DELETE_DOMINATION_FORM_API,
  DELETE_ALL_DOMINATION_FORM_API,
  GET_ALL_ABDL_FROMS_API,
  DELETE_ABDL_FORM_API,
  DELETE_ALL_ABDL_FORM_API,
} from '../../../utils/ApiRoutes';
// Components
import ConfirmModal from '../../modals/ConfirmModal';
import LoadingSpinner from '../../utils/LoadingSpinner';
import DominationContactFormItem from './DominationContactFormItem';
import AbdlContactFormItem from './AbdlContactFormItem';

function DominationAndAbdlFormAdminPageMainContainer() {
  const [dominationForms, setDominationForms] = useState([]);
  const [abdlForms, setAbdlForms] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [modalContent, setModalContent] = useState({ header: '', message: '' });
  const [loadingDomForms, setLoadingDomForms] = useState(false);
  const [loadingBabyForms, setLoadingBabyForms] = useState(false);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = () => {
    // Fetch Domination forms
    setLoadingDomForms(true);
    client
      .get(GET_ALL_DOMINATION_FROMS_API, true)
      .then((res) => {
        const list = res?.data?.dominationForms ?? [];
        setDominationForms(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error('Failed to load domination forms', err);
        setDominationForms([]);
      })
      .finally(() => setLoadingDomForms(false));

    // Fetch ABDL forms
    setLoadingBabyForms(true);
    client
      .get(GET_ALL_ABDL_FROMS_API, true)
      .then((res) => {
        const list = res?.data?.babyForms ?? [];
        setAbdlForms(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error('Failed to load abdl forms', err);
        setAbdlForms([]);
      })
      .finally(() => setLoadingBabyForms(false));
  };

  const confirmAction = ({ action, header, message }) => {
    setPendingAction(() => action);
    setModalContent({ header, message });
    setModalOpen(true);
  };

  const handleModalConfirm = () => {
    if (pendingAction) pendingAction();
    setModalOpen(false);
  };

  const handleDeleteDomination = (id) => {
    confirmAction({
      header: 'Delete Domination Form',
      message: 'Are you sure you want to delete this domination form?',
      action: () => {
        client
          .delete(`${DELETE_DOMINATION_FORM_API}/${id}`, true)
          .then(() => {
            setDominationForms((prev) => prev.filter((form) => form.id !== id));
          })
          .catch((err) =>
            console.error('Failed to delete domination form', err)
          );
      },
    });
  };

  const handleDeleteAllDomination = () => {
    confirmAction({
      header: 'Delete All Domination Forms',
      message:
        'Are you sure you want to delete ALL domination forms? This cannot be undone.',
      action: () => {
        client
          .delete(DELETE_ALL_DOMINATION_FORM_API, true)
          .then(() => setDominationForms([]))
          .catch((err) =>
            console.error('Failed to delete all domination forms', err)
          );
      },
    });
  };

  const handleDeleteAbdl = (id) => {
    confirmAction({
      header: 'Delete ABDL Form',
      message: 'Are you sure you want to delete this ABDL form?',
      action: () => {
        client
          .delete(`${DELETE_ABDL_FORM_API}/${id}`, true)
          .then(() => {
            setAbdlForms((prev) => prev.filter((form) => form.id !== id));
          })
          .catch((err) => console.error('Failed to delete abdl form', err));
      },
    });
  };

  const handleDeleteAllAbdl = () => {
    confirmAction({
      header: 'Delete All ABDL Forms',
      message:
        'Are you sure you want to delete ALL ABDL forms? This cannot be undone.',
      action: () => {
        client
          .delete(DELETE_ALL_ABDL_FORM_API, true)
          .then(() => setAbdlForms([]))
          .catch((err) =>
            console.error('Failed to delete all abdl forms', err)
          );
      },
    });
  };

  // Generic renderer that accepts the correct item component
  const renderForms = (forms, ItemComponent, handleDelete) => {
    if (!forms || forms.length === 0) {
      return <p>No forms found.</p>;
    }

    return (
      <ul className="grid gap-y-2">
        {forms.map((form, index) => (
          <li key={form.id ?? index}>
            <ItemComponent form={form} handleDelete={handleDelete} />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <main
      role="main"
      className="grid w-full h-full pt-[102px]"
      aria-label="Admin forms page"
    >
      <section className="grid w-full">
        <div className="grid gap-y-10 w-full px-8 py-8 lg:container lg:mx-auto">
          {/* Domination Forms Section */}
          <section aria-label="Domination Forms Admin Panel">
            <div className="grid grid-flow-col items-center justify-between bg-colour5 px-4 py-2 rounded-xl">
              <h2 className="text-xl font-bold text-colour1">Domination Forms</h2>
              {dominationForms.length > 0 && (
                <button
                  onClick={handleDeleteAllDomination}
                  className="bg-red-500 hover:bg-red-700 text-colour1 font-bold py-2 px-4 rounded"
                >
                  Delete All
                </button>
              )}
            </div>
            {loadingDomForms ? (
              <div className="grid w-full items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="p-2">
                {renderForms(dominationForms, DominationContactFormItem, handleDeleteDomination)}
              </div>
            )}
          </section>

          {/* ABDL Forms Section */}
          <section aria-label="ABDL Forms Admin Panel">
            <div className="grid grid-flow-col items-center justify-between bg-colour5 px-4 py-2 rounded-xl">
              <h2 className="text-xl font-bold text-colour1">ABDL Forms</h2>
              {abdlForms.length > 0 && (
                <button
                  onClick={handleDeleteAllAbdl}
                  className="bg-red-500 hover:bg-red-700 text-colour1 font-bold py-2 px-4 rounded"
                >
                  Delete All
                </button>
              )}
            </div>
            {loadingBabyForms ? (
              <div className="grid w-full items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="p-2">
                {renderForms(abdlForms, AbdlContactFormItem, handleDeleteAbdl)}
              </div>
            )}
          </section>
        </div>
      </section>

      {/* Confirm Modal */}
      {modalOpen && (
        <ConfirmModal
          onClose={() => setModalOpen(false)}
          onConfirm={handleModalConfirm}
          header={modalContent.header}
          message={modalContent.message}
        />
      )}
    </main>
  );
}

export default DominationAndAbdlFormAdminPageMainContainer;
